import { motion } from 'framer-motion';
import { useState } from 'react';

const C = { roy: '#85BB65', royDark: '#5E9444', bg: '#F0F2F0', surface: '#FAFBFA', border: '#D8DDD8', text: '#1A1E1A', text2: '#4A5248', text3: '#8A948A' };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

const benefits = [
  { icon: '⚡', title: 'Up in minutes',       desc: 'No hardware, no developers, no setup fee. Register and start rewarding customers today.' },
  { icon: '🎟️', title: 'Points + stamp cards', desc: 'Run points per £1 spent alongside stamp card rewards — collect 10, get a free coffee.' },
  { icon: '🔄', title: 'Shared platform',      desc: 'Your customers can earn from any Korunto store, giving them more reason to stay loyal to you.' },
  { icon: '📊', title: 'Live analytics',        desc: 'See redemptions, visits, and tier distribution in real time from your dashboard.' },
  { icon: '🎯', title: 'Promotions',            desc: 'Run double-points weekends, happy hour multipliers, and welcome bonuses with one click.' },
  { icon: '🆓', title: '30-day free trial',    desc: 'Every new vendor gets 5,000 points/month free for 30 days. No card required.' },
];

function Field({ label, value, name, onChange, placeholder, type = 'text', required }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 11, color: C.text3, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
        {label}{required && <span style={{ color: C.roy }}> *</span>}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange}
        placeholder={placeholder} required={required}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ width: '100%', background: C.bg, border: `1.5px solid ${focused ? C.roy : C.border}`, color: C.text, padding: '10px 13px', fontFamily: "'Open Sans', sans-serif", fontSize: 13, borderRadius: 8, outline: 'none', transition: 'border-color 0.15s', boxShadow: focused ? 'rgba(133,187,101,0.15) 0 0 0 3px' : 'none', boxSizing: 'border-box' }}
      />
    </div>
  );
}

const BUSINESS_TYPES = ['Café / Coffee shop', 'Restaurant / Takeaway', 'Retail shop', 'Salon / Barber', 'Gym / Fitness', 'Pub / Bar', 'Hotel / Accommodation', 'Other'];

export default function VendorApply() {
  const [form, setForm]     = useState({ businessName: '', businessType: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError]   = useState('');

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async () => {
    setError('');
    if (!form.businessName || !form.email || !form.password) return setError('Business name, email and password are required.');
    if (form.password.length < 8) return setError('Password must be at least 8 characters.');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');

    setStatus('loading');
    try {
      const res = await fetch('/auth/vendor/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: form.businessName.trim(),
          businessType: form.businessType || null,
          email:        form.email.trim().toLowerCase(),
          phone:        form.phone.trim() || null,
          password:     form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Registration failed. Please try again.'); setStatus('idle'); return; }
      setStatus('success');
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
      setStatus('idle');
    }
  };

  return (
    <div style={{ paddingTop: 80, paddingBottom: 96, background: C.bg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>

        {/* Header */}
        <motion.div initial="hidden" animate="show" variants={stagger} style={{ textAlign: 'center', marginBottom: 72 }}>
          <motion.p variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 16 }}>
            For Vendors
          </motion.p>
          <motion.h1 variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 'clamp(36px, 7vw, 72px)', letterSpacing: '-0.03em', color: C.text, lineHeight: 0.98, margin: '0 0 20px' }}>
            Start rewarding<br /><span style={{ color: C.roy }}>your customers today.</span>
          </motion.h1>
          <motion.p variants={fadeUp} style={{ maxWidth: 520, margin: '0 auto', color: C.text3, fontSize: 15, lineHeight: 1.8, fontFamily: "'Open Sans', sans-serif" }}>
            Set up your loyalty programme in minutes. No hardware, no card printer, no technical knowledge needed.
          </motion.p>
          <motion.div variants={fadeUp} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 28, padding: '10px 22px', borderRadius: 30, background: 'rgba(133,187,101,0.1)', border: '1.5px solid rgba(133,187,101,0.3)' }}>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 16, color: C.roy }}>30-day free trial</span>
            <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3 }}>· No card required</span>
          </motion.div>
        </motion.div>

        {/* Benefits */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 80 }}>
          {benefits.map((b, i) => (
            <motion.div key={i} variants={fadeUp} style={{ padding: '24px', borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>{b.icon}</div>
              <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>{b.title}</div>
              <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3, lineHeight: 1.7 }}>{b.desc}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Registration form */}
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            style={{ padding: '40px', borderRadius: 18, border: `1px solid ${C.border}`, background: C.surface, boxShadow: '0 4px 32px rgba(0,0,0,0.07)' }}>

            {status === 'success' ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', padding: '32px 12px' }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(133,187,101,0.15)', border: `2px solid ${C.roy}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24 }}>✓</div>
                <h3 style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 22, color: C.text, margin: '0 0 12px' }}>Check your email</h3>
                <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 14, color: C.text2, lineHeight: 1.75, margin: 0 }}>
                  We've sent a verification link to <strong>{form.email}</strong>.<br />
                  Click it to confirm your application — we'll review and approve within 24 hours.
                </p>
              </motion.div>
            ) : (
              <>
                <h2 style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 24, color: C.text, margin: '0 0 6px' }}>Create your vendor account</h2>
                <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3, margin: '0 0 28px', lineHeight: 1.6 }}>
                  Free to join · 30-day trial · No card required
                </p>

                <Field label="Business name" name="businessName" value={form.businessName} onChange={handle} placeholder="The Corner Café" required />

                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 11, color: C.text3, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Business type</label>
                  <select name="businessType" value={form.businessType} onChange={handle}
                    style={{ width: '100%', background: C.bg, border: `1.5px solid ${C.border}`, color: form.businessType ? C.text : C.text3, padding: '10px 13px', fontFamily: "'Open Sans', sans-serif", fontSize: 13, borderRadius: 8, outline: 'none', boxSizing: 'border-box' }}>
                    <option value="">Select type...</option>
                    {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <Field label="Email address" name="email"    value={form.email}    onChange={handle} placeholder="hello@yourcafe.com" type="email"    required />
                <Field label="Phone (optional)" name="phone" value={form.phone}    onChange={handle} placeholder="+44 7700 900000" type="tel" />
                <Field label="Password"       name="password"        value={form.password}        onChange={handle} placeholder="At least 8 characters" type="password" required />
                <Field label="Confirm password" name="confirmPassword" value={form.confirmPassword} onChange={handle} placeholder="Repeat your password"    type="password" required />

                {error && (
                  <div style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                    <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: '#b91c1c', margin: 0 }}>{error}</p>
                  </div>
                )}

                <button onClick={submit} disabled={status === 'loading'}
                  style={{ width: '100%', padding: '13px', marginTop: 4, background: status === 'loading' ? C.royDark : C.roy, color: '#fff', border: 'none', borderRadius: 9, fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 15, cursor: status === 'loading' ? 'not-allowed' : 'pointer', boxShadow: '0 4px 16px rgba(133,187,101,0.3)', transition: 'background 0.15s' }}>
                  {status === 'loading' ? 'Creating your account…' : 'Create Account →'}
                </button>

                <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 11, color: C.text3, textAlign: 'center', marginTop: 14, marginBottom: 0 }}>
                  By registering you agree to our vendor terms. No payment required until after your trial.
                </p>
              </>
            )}
          </motion.div>
        </div>

      </div>
    </div>
  );
}
